import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral" | "muted";

interface StatusPillProps {
  tone: StatusTone;
  children: ReactNode;
  /** Dot izquierdo on/off. Default `true`. */
  dot?: boolean;
  /** `sm` (chip inline) o `md` (uso en tabla con más padding). */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Pill de estado unificado para tablas y detalles. Un solo lugar que
 * decide cómo luce un "activo", "pausado", "borrador", etc. — así todos
 * los módulos usan la misma gramática visual.
 *
 * `tone` describe la semántica, no el color — así podemos cambiar la
 * paleta sin tocar cada feature.
 */
export function StatusPill({
  tone,
  children,
  dot = true,
  size = "sm",
  className,
}: StatusPillProps) {
  const tones: Record<StatusTone, { bg: string; text: string; dot: string }> = {
    success: {
      bg: "bg-emerald-500/10 border-emerald-600/20",
      text: "text-emerald-800 dark:text-emerald-200",
      dot: "bg-emerald-500",
    },
    warning: {
      bg: "bg-amber-500/10 border-amber-600/20",
      text: "text-amber-900 dark:text-amber-200",
      dot: "bg-amber-500",
    },
    danger: {
      bg: "bg-destructive/10 border-destructive/25",
      text: "text-destructive",
      dot: "bg-destructive",
    },
    info: {
      bg: "bg-sky-500/10 border-sky-600/20",
      text: "text-sky-800 dark:text-sky-200",
      dot: "bg-sky-500",
    },
    neutral: {
      bg: "bg-primary/10 border-primary/20",
      text: "text-primary",
      dot: "bg-primary",
    },
    muted: {
      bg: "bg-muted border-border/60",
      text: "text-muted-foreground",
      dot: "bg-muted-foreground/50",
    },
  };

  const sizes = {
    sm: "px-2 py-0.5 text-[11px] gap-1.5",
    md: "px-2.5 py-1 text-[12px] gap-2",
  };

  const t = tones[tone];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-medium",
        t.bg,
        t.text,
        sizes[size],
        className,
      )}
    >
      {dot ? (
        <span aria-hidden className={cn("inline-block h-1.5 w-1.5 rounded-full", t.dot)} />
      ) : null}
      {children}
    </span>
  );
}
